import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, ArrowLeft, Loader2, Send, Users } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import {
  CampaignBuildRequest,
  CampaignBuildResult,
  CampaignPreview,
  useBuildCampaign,
  usePreviewCampaignBuild,
} from '../../queries/marketingCampaigns';
import { FilterCondition, FilterCriteria, MarketingSegment } from '../../queries/marketingSegments';
import { useSubscriptionPackages } from '../../queries/subscriptionPackages';
import { useMarketingCampaignsEmail } from '../../queries/marketingCampaignsEmail';
import { ManualUserPicker } from './ManualUserPicker';
import { SegmentConditionEditor } from './SegmentConditionEditor';

/**
 * Builds a promo campaign in one pass: who it is for, what they get, and the
 * email that carries their code.
 *
 * The discount, segment, offer and campaign used to be four objects created
 * across three tabs before anything could be sent. They still exist and are
 * still editable there - this only stops the four visits.
 *
 * Nothing is created until the preview has been seen. Everything past that
 * point is hard or impossible to undo: a Stripe coupon, a Brevo list, and a
 * send with no recall.
 */

interface CampaignBuilderDialogProps {
  segments: MarketingSegment[];
  onClose: () => void;
}

const EMPTY_CRITERIA: FilterCriteria = { operator: 'AND', conditions: [] };

// Matches SegmentFormDialog: a condition added but never filled in carries no
// meaning, so it is dropped rather than blocking the build.
function isIncomplete({ value }: FilterCondition): boolean {
  if (Array.isArray(value)) return value.length === 0;
  return value === '' || value === null || value === undefined;
}

type RewardKind = 'discount' | 'grant';

const inputClass = 'w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm';

export function CampaignBuilderDialog({ segments, onClose }: CampaignBuilderDialogProps) {
  // Fetched here rather than by the Campaigns page, so nothing is loaded until
  // someone actually opens the builder.
  const { data: packages = [] } = useSubscriptionPackages();
  const { data: emails = [] } = useMarketingCampaignsEmail();

  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const [useSavedSegment, setUseSavedSegment] = useState(false);
  const [segmentId, setSegmentId] = useState<number | ''>('');
  const [criteria, setCriteria] = useState<FilterCriteria>(EMPTY_CRITERIA);
  const [manualUserIds, setManualUserIds] = useState<string[]>([]);

  const [rewardKind, setRewardKind] = useState<RewardKind>('discount');
  const [percentOff, setPercentOff] = useState(30);
  const [duration, setDuration] = useState<'once' | 'repeating' | 'forever'>('once');
  const [durationInMonths, setDurationInMonths] = useState(3);
  const [packageCode, setPackageCode] = useState('');
  const [accessDurationDays, setAccessDurationDays] = useState(14);

  const [emailCampaignId, setEmailCampaignId] = useState<number | ''>('');
  const [confirmSend, setConfirmSend] = useState(false);

  const [preview, setPreview] = useState<CampaignPreview | null>(null);
  const [result, setResult] = useState<CampaignBuildResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const previewBuild = usePreviewCampaignBuild();
  const build = useBuildCampaign();

  const usableConditions = criteria.conditions.filter((c) => !isIncomplete(c));
  const hasAudience = useSavedSegment ? segmentId !== '' : usableConditions.length > 0 || manualUserIds.length > 0;
  const rewardReady = rewardKind === 'discount' ? percentOff > 0 && percentOff <= 100 : Boolean(packageCode) && accessDurationDays > 0;
  const canPreview = Boolean(name.trim()) && hasAudience && rewardReady;

  const sendableEmails = emails.filter((e) => e.status !== 'archived');

  const request = (): CampaignBuildRequest => ({
    name: name.trim(),
    ...(expiresAt ? { expires_at: expiresAt } : {}),
    audience: useSavedSegment
      ? { segment_id: Number(segmentId) }
      : { conditions: { ...criteria, conditions: usableConditions }, manual_user_ids: manualUserIds },
    reward:
      rewardKind === 'discount'
        ? {
            type: 'STRIPE_DISCOUNT',
            percent_off: percentOff,
            duration,
            ...(duration === 'repeating' ? { duration_in_months: durationInMonths } : {}),
          }
        : { type: 'FREE_GRANT', package_code: packageCode, access_duration_days: accessDurationDays },
    ...(emailCampaignId === '' ? {} : { launch: { email_campaign_id: Number(emailCampaignId), confirm_send: confirmSend } }),
  });

  const handlePreview = () => {
    setError(null);
    previewBuild.mutate(request(), {
      onSuccess: setPreview,
      onError: (err) => setError(extractErrorMessage(err, 'Could not work out who this campaign would reach.')),
    });
  };

  const handleBuild = () => {
    setError(null);
    build.mutate(request(), {
      onSuccess: setResult,
      onError: (err) => setError(extractErrorMessage(err, 'The campaign could not be built.')),
    });
  };

  // Shown once it has run, so the irreversible parts are reported rather than
  // silently closing the dialog on them.
  if (result) {
    const launched = result.launched;
    return (
      <Shell title="Campaign built" onClose={onClose}>
        <div className="space-y-2 text-sm">
          <p className="font-medium">{result.campaign.name}</p>
          {result.created.discountCode && (
            <p className="text-muted-foreground">
              Stripe discount <code className="rounded bg-muted px-1">{result.created.discountCode}</code> created.
            </p>
          )}
          {launched ? (
            <>
              <p className="text-muted-foreground">
                {launched.generated} codes minted and {launched.pushed} pushed to Brevo list #{launched.brevoListId}.
              </p>
              {launched.skippedWithoutConsent > 0 && (
                <p className="text-amber-600 dark:text-amber-400">
                  {launched.skippedWithoutConsent} matched customers have not opted in to marketing and were left off the list.
                </p>
              )}
              {launched.unresolved > 0 && (
                <p className="text-amber-600 dark:text-amber-400">
                  {launched.unresolved} matched customers had no email address and were skipped.
                </p>
              )}
              <p className={launched.sent ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}>
                {launched.sent
                  ? `Email sent — ${launched.promoCodesMarkedSent} codes marked sent.`
                  : 'Not sent. The codes are staged on the Brevo list, ready to send from Marketing Emails.'}
              </p>
            </>
          ) : (
            <p className="text-muted-foreground">Left in draft. Generate the codes from the Campaigns list when you are ready.</p>
          )}
        </div>
        <div className="mt-6 flex justify-end">
          <button type="button" onClick={onClose} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90">
            Done
          </button>
        </div>
      </Shell>
    );
  }

  if (preview) {
    const willSend = Boolean(emailCampaignId !== '' && confirmSend);
    // Only a blocker that would actually break the run stops the button. "Not
    // confirmed" is a choice, not a fault, so it reads as a note.
    const blocking = preview.blockers.filter((b) => !b.startsWith('Sending is not confirmed'));
    return (
      <Shell title="Before it goes out" onClose={onClose}>
        <div className="space-y-4">
          <div className="rounded-md border border-border p-3">
            <p className="flex items-center gap-1.5 text-sm font-medium">
              <Users className="h-4 w-4" />
              {preview.deliverable} {preview.deliverable === 1 ? 'recipient' : 'recipients'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {preview.matched} matched
              {preview.undeliverable > 0 && `, ${preview.undeliverable} with no email address`}. Each gets {preview.reward}.
            </p>
            {preview.sample.length > 0 && (
              <p className="mt-2 break-words text-xs text-muted-foreground">
                e.g. {preview.sample.join(', ')}
                {preview.deliverable > preview.sample.length && ` and ${preview.deliverable - preview.sample.length} more`}
              </p>
            )}
          </div>

          <div>
            <p className="text-sm font-medium">This will create</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
              {preview.creates.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          {preview.blockers.length > 0 && (
            <div className="space-y-1 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
              {preview.blockers.map((blocker) => (
                <p key={blocker} className="flex gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {blocker}
                </p>
              ))}
            </div>
          )}

          {willSend && blocking.length === 0 && (
            <p className="text-xs text-muted-foreground">
              The send cannot be recalled once it starts.
            </p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <div className="mt-6 flex justify-between gap-2">
          <button
            type="button"
            onClick={() => setPreview(null)}
            className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
          <button
            type="button"
            onClick={handleBuild}
            disabled={build.isPending || blocking.length > 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {build.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {willSend ? `Build and send to ${preview.deliverable}` : 'Build campaign'}
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Build a campaign" onClose={onClose}>
      <p className="text-xs text-muted-foreground">
        Creates the discount, segment, offer and campaign together. Nothing is created until you have seen the preview.
      </p>

      <div className="mt-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="builder-name" className="mb-1 block text-sm font-medium">Campaign name</label>
            <input id="builder-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="builder-expiry" className="mb-1 block text-sm font-medium">Code expiry (optional)</label>
            <input
              id="builder-expiry"
              type="date"
              min={new Date().toISOString().slice(0, 10)}
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        <fieldset className="rounded-md border border-border p-3">
          <legend className="px-1 text-sm font-medium">Who it is for</legend>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={!useSavedSegment} onChange={() => setUseSavedSegment(false)} />
              Describe it here
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={useSavedSegment} onChange={() => setUseSavedSegment(true)} />
              Reuse a saved segment
            </label>
          </div>

          {useSavedSegment ? (
            <select
              value={segmentId}
              onChange={(e) => setSegmentId(e.target.value ? Number(e.target.value) : '')}
              className={`mt-3 ${inputClass}`}
              aria-label="Saved segment"
            >
              <option value="">Select a segment…</option>
              {segments.map((s) => (
                <option key={s.id} value={s.id}>{s.name} (~{s.estimated_count ?? '?'})</option>
              ))}
            </select>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Conditions</span>
                <select
                  value={criteria.operator}
                  onChange={(e) => setCriteria({ ...criteria, operator: e.target.value as 'AND' | 'OR' })}
                  className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                  aria-label="Condition operator"
                >
                  <option value="AND">Match ALL conditions</option>
                  <option value="OR">Match ANY condition</option>
                </select>
              </div>
              {criteria.conditions.map((condition, i) => (
                <SegmentConditionEditor
                  key={i}
                  condition={condition}
                  packages={packages}
                  onChange={(next) => setCriteria({ ...criteria, conditions: criteria.conditions.map((c, j) => (j === i ? next : c)) })}
                  onRemove={() => setCriteria({ ...criteria, conditions: criteria.conditions.filter((_, j) => j !== i) })}
                />
              ))}
              <button
                type="button"
                onClick={() => setCriteria({ ...criteria, conditions: [...criteria.conditions, { field: 'subscription_status', operator: 'in', value: [] }] })}
                className="rounded-md border border-input px-2 py-1 text-xs font-medium hover:bg-accent"
              >
                Add condition
              </button>
              <ManualUserPicker userIds={manualUserIds} onChange={setManualUserIds} />
            </div>
          )}
        </fieldset>

        <fieldset className="rounded-md border border-border p-3">
          <legend className="px-1 text-sm font-medium">What they get</legend>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={rewardKind === 'discount'} onChange={() => setRewardKind('discount')} />
              Stripe discount
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={rewardKind === 'grant'} onChange={() => setRewardKind('grant')} />
              Free access
            </label>
          </div>

          {rewardKind === 'discount' ? (
            <div className="mt-3 grid grid-cols-3 gap-3">
              <div>
                <label htmlFor="builder-percent" className="mb-1 block text-xs font-medium">Percent off</label>
                <input
                  id="builder-percent"
                  type="number"
                  min={1}
                  max={100}
                  value={percentOff}
                  onChange={(e) => setPercentOff(Number(e.target.value))}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="builder-duration" className="mb-1 block text-xs font-medium">Applies</label>
                <select
                  id="builder-duration"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value as typeof duration)}
                  className={inputClass}
                >
                  <option value="once">Once</option>
                  <option value="repeating">For some months</option>
                  <option value="forever">Forever</option>
                </select>
              </div>
              {duration === 'repeating' && (
                <div>
                  <label htmlFor="builder-months" className="mb-1 block text-xs font-medium">Months</label>
                  <input
                    id="builder-months"
                    type="number"
                    min={1}
                    value={durationInMonths}
                    onChange={(e) => setDurationInMonths(Number(e.target.value))}
                    className={inputClass}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="builder-package" className="mb-1 block text-xs font-medium">Package</label>
                <select id="builder-package" value={packageCode} onChange={(e) => setPackageCode(e.target.value)} className={inputClass}>
                  <option value="">Select a package…</option>
                  {packages.map((p) => (
                    <option key={p.package_code} value={p.package_code}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="builder-days" className="mb-1 block text-xs font-medium">Days of access</label>
                <input
                  id="builder-days"
                  type="number"
                  min={1}
                  value={accessDurationDays}
                  onChange={(e) => setAccessDurationDays(Number(e.target.value))}
                  className={inputClass}
                />
              </div>
            </div>
          )}
          {rewardKind === 'discount' && (
            <p className="mt-2 text-xs text-muted-foreground">
              The code is random — it never spells out the campaign, because recipients forward it.
            </p>
          )}
        </fieldset>

        <fieldset className="rounded-md border border-border p-3">
          <legend className="px-1 text-sm font-medium">The email (optional)</legend>
          <select
            value={emailCampaignId}
            onChange={(e) => setEmailCampaignId(e.target.value ? Number(e.target.value) : '')}
            className={inputClass}
            aria-label="Marketing email"
          >
            <option value="">Don&apos;t send yet — leave it in draft</option>
            {/* Sent emails stay listed but are labelled: the send refuses a
                second run, and the preview says so before anything is made. */}
            {sendableEmails.map((e) => (
              <option key={e.id} value={e.id}>
                {e.campaign_name} — {e.subject_line}
                {e.status === 'sent' ? ' (already sent)' : ''}
              </option>
            ))}
          </select>
          {emailCampaignId !== '' && (
            <label className="mt-3 flex items-start gap-2 text-sm">
              <input type="checkbox" checked={confirmSend} onChange={(e) => setConfirmSend(e.target.checked)} className="mt-0.5" />
              <span>
                Send it as soon as the codes are pushed.
                <span className="block text-xs text-muted-foreground">Leave unticked to stage the list and send it by hand.</span>
              </span>
            </label>
          )}
          {sendableEmails.length === 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              No marketing emails yet. Write one on the Marketing Emails tab — it must include the promo-code merge tag.
            </p>
          )}
        </fieldset>
      </div>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
          Cancel
        </button>
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewBuild.isPending || !canPreview}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {previewBuild.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Preview
        </button>
      </div>
    </Shell>
  );
}

function Shell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{title}</Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
